import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Testimonial {
  id: string;
  person_name: string;
  testimonial_text: string;
  image_url: string | null;
  display_order: number;
}

// Fallback testimonials when database is empty
const fallbackTestimonials = [
  {
    id: "fallback-1",
    person_name: "Sarah Mitchell",
    testimonial_text: "During one of the hardest times in my life, Living Tribute made it so easy to create something beautiful for my father. The guidance was gentle, thoughtful, and helped us tell his story perfectly.",
    image_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80",
    display_order: 0
  },
  {
    id: "fallback-2",
    person_name: "James Rodriguez",
    testimonial_text: "I was amazed at how quickly we could gather photos from family all over the country. The tribute video brought everyone to tears—in the best way possible. It captured Mom's spirit beautifully.",
    image_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80",
    display_order: 1
  },
  {
    id: "fallback-3",
    person_name: "Lisa Thompson",
    testimonial_text: "The compassion built into every detail of this platform is remarkable. It felt like having a caring friend guide us through the process when we needed it most.",
    image_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80",
    display_order: 2
  },
  {
    id: "fallback-4",
    person_name: "Michael Chen",
    testimonial_text: "Our whole family contributed memories and photos. The collaboration feature made it so special—everyone felt like they were part of honoring Grandpa's legacy together.",
    image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80",
    display_order: 3
  },
];

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dbTestimonials, setDbTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotationOffset, setRotationOffset] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const { data, error } = await supabase
          .from('testimonials')
          .select('id, person_name, testimonial_text, image_url, display_order')
          .eq('is_enabled', true)
          .order('display_order', { ascending: true });

        if (error) throw error;
        setDbTestimonials(data || []);
        
        // Generate a deterministic rotation offset based on the day
        // This ensures consistent rotation that changes daily
        const today = new Date();
        const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
        setRotationOffset(dayOfYear);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
      setLoading(false);
    };

    fetchTestimonials();
  }, []);

  // Determine which testimonials to show
  const displayTestimonials = useMemo(() => {
    if (loading) return fallbackTestimonials.slice(0, 4);
    
    if (dbTestimonials.length === 0) {
      return fallbackTestimonials.slice(0, 4);
    }

    // If 4 or fewer enabled testimonials, show all of them
    if (dbTestimonials.length <= 4) {
      return dbTestimonials;
    }

    // More than 4: rotate through based on deterministic offset
    const startIndex = rotationOffset % dbTestimonials.length;
    const selected: Testimonial[] = [];
    for (let i = 0; i < 4; i++) {
      const idx = (startIndex + i) % dbTestimonials.length;
      selected.push(dbTestimonials[idx]);
    }
    return selected;
  }, [dbTestimonials, loading, rotationOffset]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % displayTestimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + displayTestimonials.length) % displayTestimonials.length);
  };

  return (
    <section className="py-24 warm-gradient">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Trusted by Families Who Want to Celebrate Life
          </h2>
          <p className="text-lg text-muted-foreground">
            Not struggle with logistics
          </p>
        </div>
        
        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {displayTestimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="bg-card rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
            >
              <Quote className="h-8 w-8 text-accent/30 mb-4" />
              <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-grow">
                "{testimonial.testimonial_text}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                {testimonial.image_url && (
                  <img
                    src={testimonial.image_url}
                    alt={testimonial.person_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="font-semibold text-foreground text-sm">
                    {testimonial.person_name}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {displayTestimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-2">
                  <div className="bg-card rounded-2xl p-6 shadow-sm">
                    <Quote className="h-8 w-8 text-accent/30 mb-4" />
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      "{testimonial.testimonial_text}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                      {testimonial.image_url && (
                        <img
                          src={testimonial.image_url}
                          alt={testimonial.person_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-foreground">
                          {testimonial.person_name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <Button
              variant="outline"
              size="icon"
              onClick={prevSlide}
              className="rounded-full"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex gap-2" role="tablist" aria-label="Testimonial slides">
              {displayTestimonials.map((testimonial, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-accent" : "bg-border"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}: ${testimonial.person_name}`}
                  aria-selected={index === currentIndex}
                  role="tab"
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={nextSlide}
              className="rounded-full"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};